import type { DesktopHistorySidebarConfig } from "@/features/history/desktopHistorySidebar";
import type { HistoryModeSelectedEvent } from "@/features/history/historyEvents";
import type { HistoryMode } from "@/features/history/historyMode";
import {
  createMediator,
  type EventDispatchResult,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export interface HistorySidebarState extends DesktopHistorySidebarConfig {}

export type HistorySidebarEvent =
  | HistoryModeSelectedEvent
  | { readonly type: "history/sidebar-toggle-requested" }
  | {
      readonly type: "history/sidebar-visibility-requested";
      readonly visible: boolean;
    }
  | {
      readonly type: "history/sidebar-sort-changed";
      readonly sortByNewRes: boolean;
    }
  | { readonly type: "history/sidebar-refresh-requested" }
  | {
      readonly type: "history/sidebar-item-remove-requested";
      readonly mode: HistoryMode;
      readonly boardSlug: string;
      readonly threadId: string;
    }
  | {
      readonly type: "history/sidebar-clear-requested";
      readonly mode: HistoryMode;
      readonly boardSlugs: readonly string[];
    };

export type HistorySidebarEffect =
  | {
      readonly type: "persist-config";
      readonly config: DesktopHistorySidebarConfig;
    }
  | { readonly type: "refresh" }
  | {
      readonly type: "remove-item";
      readonly mode: HistoryMode;
      readonly boardSlug: string;
      readonly threadId: string;
    }
  | {
      readonly type: "clear-items";
      readonly mode: HistoryMode;
      readonly boardSlugs: readonly string[];
    };

export type HistorySidebarEventNotifier = (
  event: HistorySidebarEvent,
) => EventDispatchResult<TypedEvent>["status"];

export interface HistorySidebarMediatorOptions {
  readonly initialConfig: DesktopHistorySidebarConfig;
  readonly runEffect?: (effect: HistorySidebarEffect) => void;
}

export function isHistorySidebarEvent(
  event: TypedEvent,
): event is HistorySidebarEvent {
  switch (event.type) {
    case "history/mode-selected":
      return isHistoryModeField(event);
    case "history/sidebar-toggle-requested":
    case "history/sidebar-refresh-requested":
      return true;
    case "history/sidebar-visibility-requested":
      return hasBooleanField(event, "visible");
    case "history/sidebar-sort-changed":
      return hasBooleanField(event, "sortByNewRes");
    case "history/sidebar-item-remove-requested":
      return (
        isHistoryModeField(event) &&
        hasStringField(event, "boardSlug") &&
        hasStringField(event, "threadId")
      );
    case "history/sidebar-clear-requested":
      return (
        isHistoryModeField(event) &&
        isStringArray((event as Record<string, unknown>).boardSlugs)
      );
    default:
      return false;
  }
}

export function transitionHistorySidebar(
  state: HistorySidebarState,
  event: HistorySidebarEvent,
): MediatorTransitionResult<HistorySidebarState, HistorySidebarEffect> {
  switch (event.type) {
    case "history/mode-selected": {
      const nextState = { ...state, historyMode: event.mode };
      return {
        status: "handled",
        state: nextState,
        effects:
          state.historyMode === event.mode
            ? []
            : [{ type: "persist-config", config: nextState }],
      };
    }
    case "history/sidebar-toggle-requested": {
      const nextState = { ...state, sidebarVisible: !state.sidebarVisible };
      return {
        status: "handled",
        state: nextState,
        effects: [{ type: "persist-config", config: nextState }],
      };
    }
    case "history/sidebar-visibility-requested": {
      if (state.sidebarVisible === event.visible) {
        return { status: "handled", state };
      }
      const nextState = { ...state, sidebarVisible: event.visible };
      return {
        status: "handled",
        state: nextState,
        effects: [{ type: "persist-config", config: nextState }],
      };
    }
    case "history/sidebar-sort-changed": {
      if (state.sortByNewRes === event.sortByNewRes) {
        return { status: "handled", state };
      }
      const nextState = { ...state, sortByNewRes: event.sortByNewRes };
      return {
        status: "handled",
        state: nextState,
        effects: [{ type: "persist-config", config: nextState }],
      };
    }
    case "history/sidebar-refresh-requested":
      return { status: "handled", state, effects: [{ type: "refresh" }] };
    case "history/sidebar-item-remove-requested":
      return {
        status: "handled",
        state,
        effects: [
          {
            type: "remove-item",
            mode: event.mode,
            boardSlug: event.boardSlug,
            threadId: event.threadId,
          },
        ],
      };
    case "history/sidebar-clear-requested":
      return {
        status: "handled",
        state,
        effects: [
          {
            type: "clear-items",
            mode: event.mode,
            boardSlugs: event.boardSlugs,
          },
        ],
      };
  }
}

export function createHistorySidebarMediator(
  options: HistorySidebarMediatorOptions,
): Mediator<HistorySidebarState, HistorySidebarEvent, HistorySidebarEffect> {
  return createMediator<
    HistorySidebarState,
    HistorySidebarEvent,
    HistorySidebarEffect
  >(options.initialConfig, transitionHistorySidebar, {
    runEffect: options.runEffect,
  });
}

function isHistoryModeField(
  event: TypedEvent,
): event is TypedEvent & { readonly mode: HistoryMode } {
  const mode = (event as Record<string, unknown>).mode;
  return mode === "viewed" || mode === "posted";
}

function hasBooleanField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "boolean";
}

function hasStringField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "string";
}

function isStringArray(value: unknown): value is readonly string[] {
  return (
    Array.isArray(value) &&
    value.every((item: unknown): item is string => typeof item === "string")
  );
}
